import { Plus, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  TOOL_FREQUENCIES,
  TOOL_FREQUENCY_LABELS,
  TOOL_LEVELS,
  TOOL_LEVEL_LABELS,
  type TemplateBox,
  type ToolRow,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Segmented } from '../../../components/Segmented';
import styles from './ToolRows.module.css';

/**
 * Evening review 2026-11-01 item 6: box 1 of the Ideal candidate profile, made simpler to look at. Four drop-downs side by side
 * read like a spreadsheet; here each tool is one card: its name on top, then three one-click choices (how often, the level on
 * day one, test it or not) that show every option at once. Same rows, same rules (ADR 0083: min and max from the template, the
 * required rows marked *, a half-filled row is a problem until finished or cleared).
 */

const YES_NO = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
] as const;

const emptyTool = (): ToolRow => ({ tool: '', frequency: null, level: null, test: null });

function isEmpty(row: ToolRow): boolean {
  return row.tool.trim() === '' && row.frequency === null && row.level === null && row.test === null;
}

interface ToolRowsProps {
  readonly box: TemplateBox;
  readonly rows: readonly ToolRow[];
  readonly onChange: (rows: ToolRow[]) => void;
  readonly disabled?: boolean;
  /** The tool's name box; a plain input unless the caller gives one that suggests names. */
  readonly renderName?: (props: { readonly value: string; readonly ariaLabel: string; readonly placeholder: string; readonly disabled: boolean; readonly onChange: (value: string) => void }) => ReactNode;
}

export function ToolRows({ box, rows, onChange, disabled = false, renderName }: ToolRowsProps): ReactNode {
  const shown = Math.min(box.max, Math.max(box.min, 1, rows.length));
  const padded = Array.from({ length: shown }, (_, index) => rows[index] ?? emptyTool());
  const change = (index: number, next: Partial<ToolRow>): void => onChange(padded.map((row, at) => (at === index ? { ...row, ...next } : row)));

  return (
    <div className={styles.list}>
      {padded.map((row, index) => {
        const isRequired = index < box.min;
        const canRemove = !disabled && !isRequired && (index >= Math.max(box.min, 1) || !isEmpty(row));
        const nameProps = {
          value: row.tool,
          ariaLabel: `Tool or system, row ${index + 1}`,
          placeholder: index === 0 ? 'Google Sheets' : 'Tool or system',
          disabled,
          onChange: (value: string) => change(index, { tool: value }),
        };
        return (
          <div key={index} className={styles.card}>
            <div className={styles.top}>
              <span className={styles.number}>
                {index + 1}
                {isRequired && <span className={styles.star} aria-label="required"> *</span>}
              </span>
              <div className={styles.name}>
                {renderName ? (
                  renderName(nameProps)
                ) : (
                  <Input aria-label={nameProps.ariaLabel} disabled={disabled} placeholder={nameProps.placeholder} value={row.tool} onChange={(event) => nameProps.onChange(event.target.value)} />
                )}
              </div>
              {canRemove && <Button variant="ghost" size="sm" icon={Trash2} aria-label={`Remove row ${index + 1}`} onClick={() => onChange(padded.filter((_, at) => at !== index))} />}
            </div>
            <div className={styles.choices}>
              <div className={styles.choice}>
                <span className={styles.label}>How often</span>
                <Segmented<string>
                  label={`How often, row ${index + 1}`}
                  value={row.frequency ?? ''}
                  disabled={disabled}
                  options={TOOL_FREQUENCIES.map((value) => ({ value, label: TOOL_FREQUENCY_LABELS[value] }))}
                  onChange={(value) => change(index, { frequency: value === row.frequency ? null : (value as ToolRow['frequency']) })}
                />
              </div>
              <div className={styles.choice}>
                <span className={styles.label}>Level on day one</span>
                <Segmented<string>
                  label={`Level on day one, row ${index + 1}`}
                  value={row.level ?? ''}
                  disabled={disabled}
                  options={TOOL_LEVELS.map((value) => ({ value, label: TOOL_LEVEL_LABELS[value] }))}
                  onChange={(value) => change(index, { level: value === row.level ? null : (value as ToolRow['level']) })}
                />
              </div>
              <div className={styles.choice}>
                <span className={styles.label}>Test it?</span>
                <Segmented<string>
                  label={`Test it, row ${index + 1}`}
                  value={row.test === null ? '' : row.test ? 'yes' : 'no'}
                  disabled={disabled}
                  options={YES_NO}
                  onChange={(value) => change(index, { test: value === 'yes' })}
                />
              </div>
            </div>
          </div>
        );
      })}
      {!disabled && padded.length < box.max && (
        <div>
          <Button variant="ghost" size="sm" icon={Plus} onClick={() => onChange([...padded, emptyTool()])}>
            Add a row
          </Button>
        </div>
      )}
    </div>
  );
}
